/** Rubrique Plan de cave : espaces (cartes), édition dimensions + exceptions, aperçu, étiquettes QR. */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, QrCode, Trash2, Camera } from 'lucide-react';
import { toast } from 'sonner';
import { parametresApi } from '../../../services/api';
import { fileToBase64 } from '../../../utils/image';
import { Button, Spinner, Card } from '../../../components/ui/kit';

const STORAGE = { casiers: 'Casiers', etageres: 'Étagères', box: 'Box', cartons: 'Cartons', frigo: 'Frigo', armoire: 'Armoire', sol: 'Au sol', palette: 'Palette' };

const EMPTY = { name: '', short_code: '', storage_type: 'casiers', dimensions: { rows: 1, shelves_per_row: 1, cells_per_shelf: 1, bottles_per_cell: 6 }, exceptions: [] };

export default function PlanCave({ readOnly }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const { data: spaces, isLoading } = useQuery({ queryKey: ['spaces'], queryFn: parametresApi.spaces });
  const save = useMutation({ mutationFn: (s) => parametresApi.saveSpace(s), onSuccess: () => { toast.success('Espace enregistré'); setEditing(null); qc.invalidateQueries({ queryKey: ['spaces'] }); } });
  const del = useMutation({ mutationFn: (id) => parametresApi.deleteSpace(id), onSuccess: () => qc.invalidateQueries({ queryKey: ['spaces'] }) });

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    toast.info('Analyse de la photo…');
    try {
      const b64 = await fileToBase64(file);
      const s = await parametresApi.suggestSpace(b64);
      setEditing({ ...EMPTY, ...editing, storage_type: s.storage_type || 'casiers', dimensions: { rows: s.rows || 1, shelves_per_row: s.shelves_per_row || 1, cells_per_shelf: s.cells_per_shelf || 1, bottles_per_cell: s.bottles_per_cell || 6 } });
      toast.success('Structure proposée par l\'IA');
    } catch { toast.error('Échec analyse photo'); }
  };

  if (isLoading) return <Spinner />;

  return (
    <div className="max-w-[900px]">
      <h2 className="font-title text-3xl mb-1">Décrivez votre cave</h2>
      <p className="text-[13px] text-n600 mb-5 max-w-[560px]">Un espace = une pièce ou un meuble. Chaque espace se découpe à votre façon ; les noms servent sur les étiquettes QR et dans les sorties.</p>

      {!editing && (
        <div className="grid sm:grid-cols-2 gap-4 mb-4" data-testid="spaces-grid">
          {spaces.map((s) => (
            <Card key={s.id} className="p-4 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setEditing(s)} data-testid={`space-${s.id}`}>
              <div className="flex items-start justify-between">
                <div><p className="font-title text-xl">{s.name}</p><p className="text-[12px] text-n600">{STORAGE[s.storage_type]} · {s.short_code}</p></div>
                {!readOnly && <button onClick={(e) => { e.stopPropagation(); del.mutate(s.id); }} className="text-n400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>}
              </div>
              <p className="text-[13px] text-n700 mt-2 nums">{s.occupied} / {s.capacity} btl · {s.locations?.length || 0} emplacements</p>
              <div className="h-1.5 bg-surface rounded-full mt-2 overflow-hidden"><div className="h-full bg-gold" style={{ width: `${s.capacity ? Math.min(100, s.occupied / s.capacity * 100) : 0}%` }} /></div>
            </Card>
          ))}
          {!readOnly && (
            <Card className="p-4 flex items-center justify-center gap-4 border-dashed">
              <Button variant="ghost" onClick={() => setEditing({ ...EMPTY })} data-testid="add-space"><Plus className="w-4 h-4" /> Ajouter un espace</Button>
              <label className="vd-btn vd-btn-ghost cursor-pointer" data-testid="photo-space"><Camera className="w-4 h-4" /> Photographier<input type="file" accept="image/*" className="hidden" onChange={onPhoto} /></label>
            </Card>
          )}
        </div>
      )}

      {editing && (
        <Card className="p-5" data-testid="space-editor">
          <div className="grid sm:grid-cols-2 gap-3 mb-3">
            <label className="block"><span className="kicker block mb-1">Nom</span><input className="vd-input h-9 w-full" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="space-name" /></label>
            <label className="block"><span className="kicker block mb-1">Code court</span><input className="vd-input h-9 w-full" value={editing.short_code} onChange={(e) => setEditing({ ...editing, short_code: e.target.value.toUpperCase() })} data-testid="space-code" /></label>
            <label className="block sm:col-span-2"><span className="kicker block mb-1">Type de rangement</span>
              <select className="vd-input h-9 w-full" value={editing.storage_type} onChange={(e) => setEditing({ ...editing, storage_type: e.target.value })}>
                {Object.entries(STORAGE).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </label>
          </div>
          <p className="kicker mb-1.5">Dimensions</p>
          <div className="grid grid-cols-4 gap-2 mb-4">
            {[['rows', 'Rangées'], ['shelves_per_row', 'Étagères/rangée'], ['cells_per_shelf', 'Casiers/étagère'], ['bottles_per_cell', 'Btl/casier']].map(([k, lbl]) => (
              <label key={k} className="block"><span className="text-[11px] text-n600 block mb-1">{lbl}</span>
                <input type="number" min="1" className="vd-input h-9 w-full" value={editing.dimensions[k]} onChange={(e) => setEditing({ ...editing, dimensions: { ...editing.dimensions, [k]: Number(e.target.value) } })} data-testid={`dim-${k}`} />
              </label>
            ))}
          </div>
          <p className="text-[13px] text-n700 mb-4">Capacité : <strong className="nums">{editing.dimensions.rows * editing.dimensions.shelves_per_row * editing.dimensions.cells_per_shelf * editing.dimensions.bottles_per_cell} btl</strong> · {editing.dimensions.rows * editing.dimensions.shelves_per_row * editing.dimensions.cells_per_shelf} emplacements</p>
          <div className="flex flex-wrap gap-2">
            <Button variant="primary" onClick={() => save.mutate(editing)} disabled={!editing.name || !editing.short_code} data-testid="space-save">Enregistrer l'espace</Button>
            {editing.id && <Button variant="secondary" onClick={() => parametresApi.downloadLabels(editing.id)} data-testid="print-labels"><QrCode className="w-4 h-4" /> Imprimer les étiquettes QR</Button>}
            <Button variant="ghost" onClick={() => setEditing(null)}>Annuler</Button>
          </div>
        </Card>
      )}
    </div>
  );
}
